import React, {useEffect, useRef, useState} from "react";
import {Tab, Tabs} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {NavBarLateral} from "../NavBarLateral";
import {history} from "../../../_helpers";
import alertService from "../../../_services/cart/alert.service";
import tiendaService from "../../../_services/cart/tienda.service";

/**
 * Mi cuenta > Mi tienda: la tienda propia de la cuenta (pepe.deautopartes.ar).
 *
 * Mismo criterio que la configuracion de tienda de clasero: un solo lugar con todo lo particular de la
 * tienda, en solapas. General (direccion y si sale en el sitio general), Apariencia (logo y colores),
 * Contacto, Catalogo (que categorias y tipos se ven, solo para cuentas de nuevos) y Clientes (los
 * compradores que se registraron en la tienda, que son de la tienda nomas).
 *
 * Se puede ir completando sin el plan; la direccion se elige y la tienda se ve recien con el plan
 * Tienda propia (o su prueba gratis).
 */

const CAMPOS = ["subdominio", "mostrarEnGeneral", "nombreTienda", "descripcionCorta", "colorPrimario",
    "colorSecundario", "whatsapp", "instagram", "facebook", "emailContacto", "telefono"];

const formDe = (config) => {
    const f = {};
    CAMPOS.forEach((c) => (f[c] = config?.[c] ?? (c === "mostrarEnGeneral" ? true : "")));
    return f;
};

/** Chequea la direccion mientras se escribe, con un respiro para no consultar en cada tecla. */
const useDisponibilidad = (subdominio, actual) => {
    const [estado, setEstado] = useState(null);
    useEffect(() => {
        const s = (subdominio || "").trim().toLowerCase();
        if (!s || s === actual) {
            setEstado(null);
            return;
        }
        setEstado({chequeando: true});
        const t = setTimeout(() => {
            tiendaService.disponible(s)
                .then((r) => setEstado({disponible: r.disponible, motivo: r.motivo}))
                .catch(() => setEstado(null));
        }, 400);
        return () => clearTimeout(t);
    }, [subdominio, actual]);
    return estado;
};

const Campo = ({etiqueta, ayuda, children, ancho}) => (
    <label className={ancho ? "tienda-config__campo tienda-config__campo--ancho" : "tienda-config__campo"}>
        <span className="cuenta-usuarios__label">{etiqueta}</span>
        {children}
        {ayuda && <span className="tienda-config__ayuda">{ayuda}</span>}
    </label>
);

const Color = ({etiqueta, valor, onChange}) => (
    <Campo etiqueta={etiqueta}>
        <span className="tienda-config__color">
            <input type="color" value={valor || "#000000"} onChange={(e) => onChange(e.target.value)}/>
            <input type="text" value={valor || ""} placeholder="El del sitio" maxLength={7}
                   onChange={(e) => onChange(e.target.value)}/>
            {valor && (
                <button type="button" className="button" data-type="complementary-small" onClick={() => onChange("")}>
                    Quitar
                </button>
            )}
        </span>
    </Campo>
);

export const ConfigurarTienda = () => {
    const [config, setConfig] = useState(null);
    const [form, setForm] = useState(formDe(null));
    const [guardando, setGuardando] = useState(false);
    const [solapa, setSolapa] = useState("general");
    const archivoRef = useRef(null);

    const disponibilidad = useDisponibilidad(form.subdominio, config?.subdominio);

    useEffect(() => {
        tiendaService.config()
            .then((c) => {
                setConfig(c);
                setForm(formDe(c));
            })
            .catch((err) => alertService.error(err));
    }, []);

    const set = (campo) => (e) => {
        const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
        setForm((f) => ({...f, [campo]: v}));
    };

    const guardar = () => {
        if (disponibilidad && disponibilidad.disponible === false) {
            alertService.error(disponibilidad.motivo);
            return;
        }
        setGuardando(true);
        tiendaService.guardar(form)
            .then((c) => {
                setConfig(c);
                setForm(formDe(c));
                alertService.msg("Tienda guardada", "success");
            })
            .catch((err) => alertService.error(err))
            .finally(() => setGuardando(false));
    };

    const subirLogo = (e) => {
        const archivo = e.target.files && e.target.files[0];
        e.target.value = "";
        if (!archivo) return;
        tiendaService.subirLogo(archivo)
            .then((c) => {
                setConfig(c);
                alertService.msg("Logo actualizado", "success");
            })
            .catch((err) => alertService.error(err));
    };

    const quitarLogo = () => {
        tiendaService.quitarLogo().then(setConfig).catch((err) => alertService.error(err));
    };

    if (!config) {
        return (
            <div className="dashboard">
                <NavBarLateral handleClose={() => {}}/>
                <div className="dashboard__content cuenta-usuarios">
                    <p className="panel__estado-carga">Cargando…</p>
                </div>
            </div>
        );
    }

    const botonGuardar = (
        <div className="tienda-config__acciones">
            <button type="button" className="button" data-type="primary" disabled={guardando} onClick={guardar}>
                {guardando ? "Guardando…" : "Guardar cambios"}
            </button>
        </div>
    );

    return (
        <div className="dashboard">
            <NavBarLateral handleClose={() => {}}/>

            <div className="dashboard__content cuenta-usuarios tienda-config">
                <div className="dashboard__content-header cuenta-usuarios__header">
                    <div className="dashboard__content-title">
                        <p className="cuenta-usuarios__eyebrow">MI CUENTA</p>
                        <h2>Mi tienda</h2>
                        <p className="panel__bajada">
                            Tu tienda online con tu dirección, tu logo y solo tus productos.
                        </p>
                    </div>
                    {config.url && config.habilitada && (
                        <a className="button" data-type="accent" href={config.url} target="_blank" rel="noreferrer">
                            <FontAwesomeIcon icon="fa-solid fa-arrow-up-right-from-square"/>
                            Ver mi tienda
                        </a>
                    )}
                </div>

                {!config.habilitada && (
                    <div className="planes__banner mi-plan__aviso">
                        <div>
                            <h3>Tu plan no incluye tienda propia</h3>
                            <p>
                                Podés ir dejando todo listo. Para elegir tu dirección y que la tienda se vea,
                                pasate al plan Tienda propia.
                            </p>
                        </div>
                        <button type="button" className="button" data-type="accent" onClick={() => history.push("/planes")}>
                            Ver planes
                        </button>
                    </div>
                )}

                <Tabs activeKey={solapa} onSelect={(k) => setSolapa(k)} className="tienda-config__solapas">
                    <Tab eventKey="general" title="General">
                        <section className="cuenta-usuarios__card tienda-config__grid">
                            <Campo etiqueta="Dirección de tu tienda" ancho
                                   ayuda="Letras, números y guiones. Si la cambiás, la dirección vieja deja de funcionar.">
                                <span className="tienda-config__subdominio">
                                    <span>https://</span>
                                    <input type="text" value={form.subdominio} onChange={set("subdominio")}
                                           placeholder="tunegocio" maxLength={63} disabled={!config.habilitada}/>
                                    <span>.{config.dominioBase}</span>
                                </span>
                                {disponibilidad?.chequeando && <span className="tienda-config__ayuda">Revisando…</span>}
                                {disponibilidad?.disponible === true && (
                                    <span className="tienda-config__ok">
                                        <FontAwesomeIcon icon="fa-solid fa-check"/> Está libre
                                    </span>
                                )}
                                {disponibilidad?.disponible === false && (
                                    <span className="tienda-config__error">{disponibilidad.motivo}</span>
                                )}
                            </Campo>

                            <Campo etiqueta="Nombre de la tienda" ancho ayuda="Es el que se ve arriba si no cargás un logo.">
                                <input type="text" value={form.nombreTienda} onChange={set("nombreTienda")}
                                       placeholder={config.nombreCuenta} maxLength={150}/>
                            </Campo>

                            <label className="tienda-config__check tienda-config__campo--ancho">
                                <input type="checkbox" checked={!!form.mostrarEnGeneral} onChange={set("mostrarEnGeneral")}/>
                                <span>
                                    <strong>Mostrar mis productos también en DeAutopartes</strong>
                                    <span className="tienda-config__ayuda">
                                        Si lo apagás, tus publicaciones se ven solo en tu tienda.
                                    </span>
                                </span>
                            </label>

                            <div className="tienda-config__campo--ancho">
                                <span className="cuenta-usuarios__label">Rubro</span>
                                <p className="tienda-config__rubro">
                                    {config.desarmadero ? "Repuestos usados (desarmadero)" : "Repuestos nuevos"}
                                    <span className="tienda-config__ayuda"> Para cambiarlo, escribinos.</span>
                                </p>
                            </div>
                        </section>
                        {botonGuardar}
                    </Tab>

                    <Tab eventKey="apariencia" title="Apariencia">
                        <section className="cuenta-usuarios__card tienda-config__grid">
                            <div className="tienda-config__campo--ancho">
                                <span className="cuenta-usuarios__label">Logo</span>
                                <div className="tienda-config__logo">
                                    {config.logoUrl
                                        ? <img src={config.logoUrl} alt="Logo de la tienda"/>
                                        : <span className="tienda-config__logo-vacio">Sin logo</span>}
                                    <div>
                                        <input ref={archivoRef} type="file" accept="image/png,image/jpeg,image/webp"
                                               hidden onChange={subirLogo}/>
                                        <button type="button" className="button" data-type="primary-small"
                                                onClick={() => archivoRef.current && archivoRef.current.click()}>
                                            {config.logoUrl ? "Cambiar logo" : "Subir logo"}
                                        </button>
                                        {config.logoUrl && (
                                            <button type="button" className="button" data-type="complementary-small"
                                                    onClick={quitarLogo}>
                                                Quitar
                                            </button>
                                        )}
                                        <span className="tienda-config__ayuda">PNG, JPG o WEBP de hasta 2 MB. Mejor apaisado y con fondo transparente.</span>
                                    </div>
                                </div>
                            </div>

                            <Color etiqueta="Color principal (botones y links)" valor={form.colorPrimario}
                                   onChange={set("colorPrimario")}/>
                            <Color etiqueta="Color secundario (destacados)" valor={form.colorSecundario}
                                   onChange={set("colorSecundario")}/>

                            <Campo etiqueta="Descripción corta" ancho ayuda="Va al pie de la tienda. Hasta 300 caracteres.">
                                <textarea rows={3} value={form.descripcionCorta} onChange={set("descripcionCorta")}
                                          maxLength={300} placeholder="Repuestos originales y alternativos para todas las marcas."/>
                            </Campo>

                            <div className="tienda-config__campo--ancho">
                                <span className="cuenta-usuarios__label">Así se ve</span>
                                <div className="tienda-config__preview">
                                    <div className="tienda-config__preview-barra">
                                        {config.logoUrl
                                            ? <img src={config.logoUrl} alt=""/>
                                            : <strong>{form.nombreTienda || config.nombreCuenta}</strong>}
                                    </div>
                                    <div className="tienda-config__preview-cuerpo">
                                        <span style={{color: form.colorSecundario || undefined}}>Destacado</span>
                                        <button type="button" className="button" data-type="primary-small"
                                                style={form.colorPrimario ? {backgroundColor: form.colorPrimario, borderColor: form.colorPrimario} : undefined}>
                                            Comprar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </section>
                        {botonGuardar}
                    </Tab>

                    <Tab eventKey="contacto" title="Contacto">
                        <section className="cuenta-usuarios__card tienda-config__grid">
                            <Campo etiqueta="WhatsApp">
                                <input type="text" value={form.whatsapp} onChange={set("whatsapp")} placeholder="5491155554444" maxLength={30}/>
                            </Campo>
                            <Campo etiqueta="Teléfono">
                                <input type="text" value={form.telefono} onChange={set("telefono")} maxLength={40}/>
                            </Campo>
                            <Campo etiqueta="Email de contacto">
                                <input type="email" value={form.emailContacto} onChange={set("emailContacto")} maxLength={150}/>
                            </Campo>
                            <Campo etiqueta="Instagram">
                                <input type="text" value={form.instagram} onChange={set("instagram")} placeholder="@tunegocio" maxLength={100}/>
                            </Campo>
                            <Campo etiqueta="Facebook" ancho>
                                <input type="text" value={form.facebook} onChange={set("facebook")} placeholder="https://facebook.com/tunegocio" maxLength={150}/>
                            </Campo>
                        </section>
                        {botonGuardar}
                    </Tab>

                    {!config.desarmadero && (
                        <Tab eventKey="catalogo" title="Catálogo">
                            <CatalogoTienda/>
                        </Tab>
                    )}

                    <Tab eventKey="clientes" title="Clientes">
                        {solapa === "clientes" && <ClientesTienda/>}
                    </Tab>
                </Tabs>
            </div>
        </div>
    );
};

/**
 * Que categorias y tipos de repuesto se ven en la tienda y se ofrecen al publicar. Se guarda lo que se
 * oculta: un tipo nuevo que agregue DeAutopartes aparece solo.
 */
const CatalogoTienda = () => {
    const [datos, setDatos] = useState(null);
    const [catOcultas, setCatOcultas] = useState(new Set());
    const [tiposOcultos, setTiposOcultos] = useState(new Set());
    const [abierta, setAbierta] = useState(null);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        tiendaService.catalogo()
            .then((r) => {
                setDatos(r);
                setCatOcultas(new Set(r.categoriasOcultas || []));
                setTiposOcultos(new Set(r.tiposOcultos || []));
            })
            .catch((err) => alertService.error(err));
    }, []);

    if (!datos) return <p className="panel__estado-carga">Cargando…</p>;

    const alternar = (set, setter, id) => {
        const nuevo = new Set(set);
        if (nuevo.has(id)) nuevo.delete(id); else nuevo.add(id);
        setter(nuevo);
    };

    const sinCategoria = (datos.tipos || []).filter((t) => !t.categoriaId);
    const grupos = [
        ...(datos.categorias || []).map((c) => ({cat: c, tipos: datos.tipos.filter((t) => t.categoriaId === c.id)})),
        ...(sinCategoria.length ? [{cat: {id: null, nombre: "Otros"}, tipos: sinCategoria}] : []),
    ];

    const guardar = () => {
        setGuardando(true);
        tiendaService.guardarCatalogo([...catOcultas], [...tiposOcultos])
            .then(() => alertService.msg("Catálogo guardado", "success"))
            .catch((err) => alertService.error(err))
            .finally(() => setGuardando(false));
    };

    return (
        <>
            <section className="cuenta-usuarios__card">
                <p className="panel__bajada">
                    Destildá lo que no vendés: deja de aparecer en los filtros de tu tienda y al cargar publicaciones.
                </p>
                <div className="tienda-catalogo">
                    {grupos.map(({cat, tipos}) => {
                        const oculta = cat.id != null && catOcultas.has(cat.id);
                        const visibles = tipos.filter((t) => !tiposOcultos.has(t.id)).length;
                        const clave = cat.id ?? "otros";
                        return (
                            <div key={clave} className="tienda-catalogo__categoria">
                                <div className="tienda-catalogo__cabecera">
                                    {cat.id != null ? (
                                        <label className="tienda-config__check">
                                            <input type="checkbox" checked={!oculta}
                                                   onChange={() => alternar(catOcultas, setCatOcultas, cat.id)}/>
                                            <strong>{cat.nombre}</strong>
                                        </label>
                                    ) : <strong>{cat.nombre}</strong>}
                                    <button type="button" className="tienda-catalogo__abrir" disabled={oculta}
                                            onClick={() => setAbierta(abierta === clave ? null : clave)}>
                                        {oculta ? "Oculta" : `${visibles} de ${tipos.length} tipos`}
                                        <FontAwesomeIcon icon={abierta === clave ? "fa-solid fa-chevron-up" : "fa-solid fa-chevron-down"}/>
                                    </button>
                                </div>
                                {abierta === clave && !oculta && (
                                    <div className="tienda-catalogo__tipos">
                                        {tipos.map((t) => (
                                            <label key={t.id} className="tienda-config__check">
                                                <input type="checkbox" checked={!tiposOcultos.has(t.id)}
                                                       onChange={() => alternar(tiposOcultos, setTiposOcultos, t.id)}/>
                                                <span>{t.nombre}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>
            <div className="tienda-config__acciones">
                <button type="button" className="button" data-type="primary" disabled={guardando} onClick={guardar}>
                    {guardando ? "Guardando…" : "Guardar catálogo"}
                </button>
            </div>
        </>
    );
};

/** Los compradores que se registraron en la tienda. Son de la tienda: no existen en DeAutopartes. */
const ClientesTienda = () => {
    const [pagina, setPagina] = useState(null);
    const [page, setPage] = useState(0);

    useEffect(() => {
        tiendaService.clientes(page).then(setPagina).catch((err) => alertService.error(err));
    }, [page]);

    if (!pagina) return <p className="panel__estado-carga">Cargando…</p>;

    return (
        <section className="cuenta-usuarios__card cuenta-usuarios__tabla-card">
            {pagina.content.length === 0 ? (
                <p className="cuenta-usuarios__vacio">Todavía no se registró nadie en tu tienda.</p>
            ) : (
                <>
                    <table className="cuenta-usuarios__tabla">
                        <thead>
                        <tr>
                            <th>Nombre</th>
                            <th>Email</th>
                            <th>Celular</th>
                            <th>Alta</th>
                        </tr>
                        </thead>
                        <tbody>
                        {pagina.content.map((c) => (
                            <tr key={c.id}>
                                <td>{c.nombre || "—"}</td>
                                <td>{c.email}</td>
                                <td>{c.celular || "—"}</td>
                                <td>{c.alta ? new Date(c.alta).toLocaleDateString("es-AR") : "—"}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    {pagina.totalPages > 1 && (
                        <div className="tienda-config__acciones">
                            <button type="button" className="button" data-type="complementary-small"
                                    disabled={pagina.first} onClick={() => setPage(page - 1)}>Anterior</button>
                            <span>{page + 1} de {pagina.totalPages}</span>
                            <button type="button" className="button" data-type="complementary-small"
                                    disabled={pagina.last} onClick={() => setPage(page + 1)}>Siguiente</button>
                        </div>
                    )}
                </>
            )}
        </section>
    );
};
